import React, { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import Reveal from "./Reveal";
import { api } from "../../lib/api";
import { trackEvent } from "../../lib/track";

/**
 * Homepage testimonials carousel.
 *
 * Continuous horizontal drift (infinite marquee). Cards scroll right
 * to left at a slow, cinematic pace using a CSS `@keyframes` translate
 * animation. The track contains the testimonial set rendered twice
 * back-to-back so the loop is seamless: when the first set has fully
 * scrolled past the viewport, the duplicate is already exactly in
 * position. Hovering anywhere over the rail pauses the drift via
 * `animation-play-state: paused`. `prefers-reduced-motion` users get
 * a static rail (animation-play-state: paused with hover-override
 * disabled). No buttons, no dots — the drift IS the affordance.
 *
 * Loading: shows a tasteful skeleton row.
 * Empty:   returns null so the section doesn't render a broken shell.
 */
const TestimonialsCarouselPremium = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/testimonials")
      .then((res) => {
        if (cancelled) return;
        // Stable display order: approved seed names first, then newer
        // approvals in API order (newest-first from the backend sort).
        const preferred = ["Amara K.", "Marcus D.", "Imani R."];
        const list = (res.data || []).slice();
        list.sort((a, b) => {
          const ai = preferred.indexOf(a.name);
          const bi = preferred.indexOf(b.name);
          if (ai === -1 && bi === -1) return 0;
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
        setItems(list);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loading && items.length === 0) return null;

  // Duplicate the items so the drift can loop seamlessly. The track
  // animates `translateX(-50%)` which lands the second set exactly
  // where the first set started, then snaps back to 0.
  const renderItems = loading ? Array.from({ length: 4 }) : items;
  const driftItems = items.length > 0 ? [...renderItems, ...renderItems] : renderItems;
  // Tune duration to the count so each card stays on screen ~10s no
  // matter how many testimonials are approved.
  const driftSeconds = Math.max(28, renderItems.length * 9);

  return (
    <section
      id="wisdom"
      data-testid="testimonials-teaser-section"
      className="relative py-24 md:py-32 border-t border-gold/10"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
            From the Path
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-themed font-light text-center max-w-3xl mx-auto">
            What clarity{" "}
            <span className="italic gold-gradient-text">sounds like.</span>
          </h2>
        </Reveal>

        <Reveal delay={100}>
          <div
            className="mt-14 relative testimonials-drift"
            data-testid="testimonials-pcarousel"
            aria-roledescription="carousel"
            aria-label="Approved testimonials, drifting"
          >
            {/* Edge fades — soft mask so cards fade in/out at the rail
                edges instead of hard-clipping at the section gutter. */}
            <div className="testimonials-drift-track-wrap">
              <div
                data-testid="testimonials-pcarousel-track"
                className="testimonials-drift-track"
                style={{ animationDuration: `${driftSeconds}s` }}
              >
                {driftItems.map((t, i) => (
                  <figure
                    key={`${t?.id || "skeleton"}-${i}`}
                    data-testid={`testimonial-pcarousel-card-${i}`}
                    className="testimonials-drift-card glass-card p-7 flex flex-col"
                    aria-hidden={i >= renderItems.length}
                  >
                    <Quote
                      size={18}
                      strokeWidth={1.4}
                      className="text-gold/60 mb-4"
                      aria-hidden
                    />
                    {t ? (
                      <>
                        <blockquote className="text-themed-muted text-[15px] leading-relaxed flex-grow">
                          {t.testimonial}
                        </blockquote>
                        <figcaption className="mt-6 pt-5 border-t border-gold/15">
                          <div className="text-themed text-sm font-serif">{t.name}</div>
                          {t.role ? (
                            <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mt-1">
                              {t.role}
                            </div>
                          ) : null}
                        </figcaption>
                      </>
                    ) : (
                      <>
                        <div className="h-3 w-3/4 mb-2 bg-gold/10 rounded" />
                        <div className="h-3 w-full mb-2 bg-gold/10 rounded" />
                        <div className="h-3 w-5/6 mb-6 bg-gold/10 rounded" />
                        <div className="h-3 w-1/3 bg-gold/15 rounded mt-auto" />
                      </>
                    )}
                  </figure>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={420}>
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/voices"
              data-testid="testimonials-teaser-voices-cta"
              onClick={() => trackEvent("testimonials_carousel_voices_cta_clicked")}
              className="btn-ghost-gold inline-flex"
            >
              Read More Voices
            </a>
            <a
              href="/voices#add-your-words"
              data-testid="testimonials-share-your-experience"
              onClick={() => trackEvent("testimonials_share_your_experience_clicked")}
              className="text-[12px] tracking-[0.22em] uppercase text-gold/85 hover:text-gold underline-offset-[6px] hover:underline transition-colors"
            >
              Add your reflection
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default TestimonialsCarouselPremium;
